import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Document, Page } from 'react-pdf';
import { GlobalWorkerOptions, version as pdfjsDistVersion } from 'pdfjs-dist';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import '../../Styles/Layout/Resources/PDFViewer.css';

// Configure worker from pdfjs-dist directly — CRA often fails to resolve `pdfjs` re-export from `react-pdf`.
GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsDistVersion}/build/pdf.worker.min.mjs`;

interface PDFViewerProps {
  content: string;
  url?: string;
}

const PDFViewer: React.FC<PDFViewerProps> = ({ content, url }) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
  }

  function onDocumentLoadError(err: Error) {
    setError(`Failed to load PDF: ${err.message}`);
    setLoading(false);
  }

  // Validate content before rendering
  useEffect(() => {
    if (!content && !url) {
      setError('No PDF content or URL provided');
      setLoading(false);
      return;
    }

    // Only validate base64 when we don't have a URL to load from
    if (!url && content) {
      if (!content.trim()) {
        setError('PDF content is empty');
        setLoading(false);
        return;
      }

      try {
        atob(content);
      } catch (e) {
        setError('PDF content is not valid base64');
        setLoading(false);
        return;
      }
    }

    setError(null);
    setLoading(true);
  }, [content, url]);

  const goToPrevPage = useCallback(() => {
    setPageNumber(prev => Math.max(1, prev - 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setPageNumber(prev => Math.min(numPages, prev + 1));
  }, [numPages]);

  const goToPage = (page: number) => {
    setPageNumber(page);
  };

  useEffect(() => {
    const isTypingTarget = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      return (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      );
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (isTypingTarget(event.target)) return;
      if (loading || numPages < 1) return;

      if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
        event.preventDefault();
        goToPrevPage();
      } else if (event.key === 'ArrowRight' || event.key === 'PageDown') {
        event.preventDefault();
        goToNextPage();
      } else if (event.key === 'Home') {
        event.preventDefault();
        setPageNumber(1);
      } else if (event.key === 'End') {
        event.preventDefault();
        setPageNumber(numPages);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToNextPage, goToPrevPage, loading, numPages]);

  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  const renderPageButtons = () => {
    const buttons = [];
    const maxVisible = 7; // Show max 7 buttons including ellipsis
    
    if (numPages <= maxVisible) {
      // Show all pages if total is small
      for (let i = 1; i <= numPages; i++) {
        buttons.push(
          <button
            key={i}
            onClick={() => goToPage(i)}
            className={`pdf-page-button ${i === pageNumber ? 'active' : ''}`}
          >
            {i}
          </button>
        );
      }
    } else {
      // Show smart pagination with ellipsis
      const startPages = [1, 2];
      const endPages = [numPages - 1, numPages];
      const currentGroup = [pageNumber - 1, pageNumber, pageNumber + 1].filter(p => p > 2 && p < numPages - 1);
      
      // First pages
      startPages.forEach(page => {
        buttons.push(
          <button
            key={page}
            onClick={() => goToPage(page)}
            className={`pdf-page-button ${page === pageNumber ? 'active' : ''}`}
          >
            {page}
          </button>
        );
      });
      
      // Ellipsis before current group
      if (currentGroup.length > 0 && currentGroup[0] > 3) {
        buttons.push(<span key="ellipsis1" className="pdf-ellipsis">...</span>);
      }
      
      // Current group
      currentGroup.forEach(page => {
        buttons.push(
          <button
            key={page}
            onClick={() => goToPage(page)}
            className={`pdf-page-button ${page === pageNumber ? 'active' : ''}`}
          >
            {page}
          </button>
        );
      });
      
      // Ellipsis after current group
      if (currentGroup.length > 0 && currentGroup[currentGroup.length - 1] < numPages - 2) {
        buttons.push(<span key="ellipsis2" className="pdf-ellipsis">...</span>);
      }
      
      // Last pages
      endPages.forEach(page => {
        buttons.push(
          <button
            key={page}
            onClick={() => goToPage(page)}
            className={`pdf-page-button ${page === pageNumber ? 'active' : ''}`}
          >
            {page}
          </button>
        );
      });
    }
    
    return buttons;
  };

  const pdfSource = url || (content ? `data:application/pdf;base64,${content}` : null);

  if (error) {
    return (
      <div className="pdf-viewer-container">
        <div className="pdf-loading">Unable to load PDF</div>
      </div>
    );
  }

  if (!pdfSource) {
    return (
      <div className="pdf-viewer-container">
        <div className="pdf-loading">Loading PDF...</div>
      </div>
    );
  }

  return (
    <div
      className="pdf-viewer-container"
      ref={containerRef}
      tabIndex={0}
      aria-label="PDF viewer"
    >
      <div className="pdf-document-container">
        <Document
          file={pdfSource}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={<div className="pdf-loading">Loading PDF...</div>}
          error={<div className="pdf-loading">Unable to load PDF</div>}
        >
          <Page
            pageNumber={pageNumber}
            renderTextLayer={true}
            renderAnnotationLayer={true}
            scale={1.2}
            loading={<div className="pdf-loading">Loading page...</div>}
          />
        </Document>
      </div>

      {!loading && numPages > 0 && (
        <div className="pdf-controls">
          <button
            onClick={goToPrevPage}
            disabled={pageNumber <= 1}
            className="pdf-nav-button"
          >
            ←
          </button>

          {renderPageButtons()}

          <button
            onClick={goToNextPage}
            disabled={pageNumber >= numPages}
            className="pdf-nav-button"
          >
            →
          </button>
        </div>
      )}
    </div>
  );
};

export default PDFViewer;